/**
 * JP Gems & Jewels - Purchase Module (inside Hariom ERP)
 * Department-wise monthly tool requirements with flexible multi-vendor price
 * comparison. Buyers add/edit/delete items (any vendor + price), pick tools,
 * the cheapest vendor is auto-selected, and a Purchase Order is generated.
 * The PO downloads as Excel or PDF, branded for JP Gems & Jewels, with items
 * grouped by vendor so each vendor gets only their part.
 */
import React, { useState, useEffect, useMemo } from 'react'
import { ShoppingBag, Plus, Trash2, Download, FileText, Search, Check, FileSpreadsheet, Edit3, X } from 'lucide-react'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import * as XLSX from 'xlsx'
import { usePurchaseStore, type POItem, type PurchaseOrder } from '../../store/purchaseStore'
import { useAuthStore } from '../../store/authStore'
import { SEED_GOODS, SEED_DEPTS, PURCHASE_SEED_VERSION, type Good } from '../../data/purchaseSeed'

const norm = (s: string) => s.toUpperCase().replace(/[^A-Z0-9]/g, '')
const bestOf = (prices: Record<string, number>) => {
  let bv = '', bp = Infinity
  for (const [v, p] of Object.entries(prices)) if (p < bp) { bp = p; bv = v }
  return bp === Infinity ? { vendor: '', price: 0 } : { vendor: bv, price: bp }
}

interface CartItem { key: string; name: string; dept: string; qty: number; vendor: string; price: number; lastPrice: number; prices: Record<string, number> }
interface VendorRow { vendor: string; price: string }
interface ItemForm { name: string; qty: string; monthlyQty: string; vendors: VendorRow[] }
const EMPTY_FORM: ItemForm = { name: '', qty: '1', monthlyQty: '', vendors: [{ vendor: 'Darshan', price: '' }] }

const Purchase: React.FC = () => {
  const { goods, depts, orders, setGoods, setDepts, addOrder, removeOrder } = usePurchaseStore()
  const { user } = useAuthStore()

  // Load the real price-list seed (204 goods + departments) ONLY on a genuine
  // first run: wait for the store to finish loading from the server, then seed
  // ONLY if it is still empty. Never overwrite — so a new item or PO added by
  // Amris can never be wiped by a re-seed.
  useEffect(() => {
    const seedIfEmpty = () => {
      const s = usePurchaseStore.getState()
      if (s.goods.length === 0 && s.seedVersion !== PURCHASE_SEED_VERSION) {
        s.setGoods(SEED_GOODS); s.setDepts(SEED_DEPTS); s.setSeedVersion(PURCHASE_SEED_VERSION)
      }
    }
    if (usePurchaseStore.persist.hasHydrated()) seedIfEmpty()
    return usePurchaseStore.persist.onFinishHydration(seedIfEmpty)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const deptNames = depts.map(d => d.dept)
  const [tab, setTab] = useState<string>('')
  const activeTab = tab || deptNames[0] || 'Setting'
  const [search, setSearch] = useState('')
  const [cart, setCart] = useState<CartItem[]>([])
  const [remarks, setRemarks] = useState('')
  const [rowQty, setRowQty] = useState<Record<string, string>>({})
  const [rowVendor, setRowVendor] = useState<Record<string, string>>({})
  // Add / Edit item modal
  const [showItemModal, setShowItemModal] = useState(false)
  const [itemForm, setItemForm] = useState<ItemForm>(EMPTY_FORM)
  const [editingName, setEditingName] = useState<string | null>(null)
  const [editingPoNo, setEditingPoNo] = useState<string | null>(null)   // when re-opening a PO to fix it

  const goodsKeyed = useMemo(() => {
    const m = new Map<string, Good>()
    goods.forEach(g => m.set(norm(g.name), g))
    return m
  }, [goods])
  const lookupGood = (toolName: string): Good | undefined => {
    const key = norm(toolName)
    if (!key) return undefined
    if (goodsKeyed.has(key)) return goodsKeyed.get(key)
    return goods.find(g => { const gk = norm(g.name); return gk.length > 4 && (gk.includes(key) || key.includes(gk)) })
  }
  const allVendors = useMemo(() => { const s = new Set<string>(); goods.forEach(g => Object.keys(g.prices).forEach(v => s.add(v))); return [...s] }, [goods])

  const lastPurchasePrice = (name: string): number | null => {
    for (const o of orders) { const it = o.items.find(i => norm(i.name) === norm(name)); if (it) return it.price }
    return null
  }
  const rowKey = (dept: string, name: string) => `${dept}|${norm(name)}`

  const addToCart = (name: string, dept: string, qtyStr: string, vendor: string, price: number, prices: Record<string, number>, last: number | null) => {
    const q = parseInt(qtyStr, 10)
    setCart(prev => [...prev, { key: `${Date.now()}-${prev.length}`, name, dept, qty: Number.isFinite(q) && q > 0 ? q : 1, vendor: vendor || 'Darshan', price: price || 0, lastPrice: last ?? (price || 0), prices }])
    toast.success(`Added ${name} to purchase order`)
  }
  const setCartField = (key: string, patch: Partial<CartItem>) => setCart(prev => prev.map(c => c.key === key ? { ...c, ...patch } : c))
  const chooseCartVendor = (key: string, vendor: string) => setCart(prev => prev.map(c => c.key === key ? { ...c, vendor, price: c.prices[vendor] ?? c.price } : c))

  // ----- Add / Edit item modal -----
  const openNewItem = () => { setItemForm({ ...EMPTY_FORM, vendors: [{ vendor: 'Darshan', price: '' }] }); setEditingName(null); setShowItemModal(true) }
  const openEditItem = (name: string) => {
    const g = lookupGood(name)
    const deptItem = depts.find(d => d.dept === activeTab)?.items.find(it => norm(it.name) === norm(name))
    setItemForm({
      name, qty: rowQty[rowKey(activeTab, name)] || '1', monthlyQty: deptItem?.qty || '',
      vendors: g && Object.keys(g.prices).length ? Object.entries(g.prices).map(([v, p]) => ({ vendor: v, price: String(p) })) : [{ vendor: 'Darshan', price: '' }],
    })
    setEditingName(name); setShowItemModal(true)
  }
  const setVendorRow = (i: number, patch: Partial<VendorRow>) => setItemForm(f => ({ ...f, vendors: f.vendors.map((v, idx) => idx === i ? { ...v, ...patch } : v) }))
  const addVendorRow = () => setItemForm(f => ({ ...f, vendors: [...f.vendors, { vendor: '', price: '' }] }))
  const removeVendorRow = (i: number) => setItemForm(f => ({ ...f, vendors: f.vendors.length > 1 ? f.vendors.filter((_, idx) => idx !== i) : f.vendors }))

  const saveItem = () => {
    const name = itemForm.name.trim()
    if (!name) { toast.error('Item name is required'); return }
    const dept = depts.find(d => d.dept === activeTab)   // undefined on the Price List / Orders tabs
    const prices: Record<string, number> = {}
    itemForm.vendors.forEach(v => { const vn = v.vendor.trim(); const p = parseFloat(v.price); if (vn && Number.isFinite(p)) prices[vn] = p })
    const best = bestOf(prices)

    if (editingName) {
      // Editing works from anywhere (incl. the Price List): always update the good;
      // update the department item too only if we're inside a department tab.
      setGoods(goods.map(g => norm(g.name) === norm(editingName) ? { ...g, name, prices, bestVendor: best.vendor, bestPrice: best.price } : g))
      if (dept) setDepts(depts.map(d => d.dept === activeTab ? { ...d, items: d.items.map(it => norm(it.name) === norm(editingName!) ? { name, qty: itemForm.monthlyQty } : it) } : d))
      toast.success(`Updated ${name}`)
    } else {
      if (!dept) { toast.error('Open a department tab first'); return }
      if (dept.items.some(it => norm(it.name) === norm(name))) { toast.error(`"${name}" already exists in ${activeTab}`); return }
      const exists = goods.some(g => norm(g.name) === norm(name))
      const newGood: Good = { id: `g-new-${Date.now()}`, name, prices, bestVendor: best.vendor, bestPrice: best.price }
      setGoods(exists ? goods.map(g => norm(g.name) === norm(name) ? { ...g, prices, bestVendor: best.vendor, bestPrice: best.price } : g) : [...goods, newGood])
      setDepts(depts.map(d => d.dept === activeTab ? { ...d, items: [...d.items, { name, qty: itemForm.monthlyQty }] } : d))
      const q = parseInt(itemForm.qty, 10); if (q > 0) setRowQty(prev => ({ ...prev, [rowKey(activeTab, name)]: String(q) }))
      toast.success(`Added ${name}`)
    }
    setShowItemModal(false)
  }
  const deleteItem = (name: string) => {
    if (!window.confirm(`Remove "${name}" from ${activeTab}?`)) return
    setDepts(depts.map(d => d.dept === activeTab ? { ...d, items: d.items.filter(it => norm(it.name) !== norm(name)) } : d))
    toast.success(`Removed ${name}`)
  }
  // Delete a tool from the Price List entirely (good + every department).
  const deleteGood = (name: string) => {
    if (!window.confirm(`Delete "${name}" from the price list (and all departments)?`)) return
    setGoods(goods.filter(g => norm(g.name) !== norm(name)))
    setDepts(depts.map(d => ({ ...d, items: d.items.filter(it => norm(it.name) !== norm(name)) })))
    toast.success(`Deleted ${name}`)
  }

  const cartTotal = cart.reduce((s, c) => s + c.qty * c.price, 0)

  // ---- JP branded PDF header ----
  const jpHeader = (doc: jsPDF) => {
    doc.setFillColor(17, 17, 17); doc.rect(0, 0, 210, 30, 'F')
    doc.setDrawColor(232, 190, 140); doc.setLineWidth(0.7)
    doc.lines([[6, 6], [-6, 6], [-6, -6]], 22, 7, [1, 1], 'S', true) // diamond
    doc.setTextColor(232, 190, 140); doc.setFont('times', 'bold'); doc.setFontSize(12); doc.text('JP', 19, 16)
    doc.setFont('times', 'bolditalic'); doc.setFontSize(20); doc.text('JP Gems & Jewels', 40, 15)
    doc.setFont('times', 'italic'); doc.setFontSize(8); doc.setTextColor(210, 180, 140); doc.text('Serving Beauty Through Jewellery', 40, 22)
  }

  // group items by vendor
  const groupByVendor = (items: POItem[]) => {
    const m: Record<string, POItem[]> = {}
    items.forEach(it => { (m[it.vendor] = m[it.vendor] || []).push(it) })
    return m
  }

  const downloadPdf = (po: PurchaseOrder) => {
    const doc = new jsPDF()
    jpHeader(doc)
    doc.setTextColor(0, 0, 0); doc.setFont('helvetica', 'normal'); doc.setFontSize(11)
    doc.text('Purchase Order', 14, 40)
    doc.setFontSize(9); doc.text(`PO No: ${po.poNo}`, 14, 46); doc.text(`Date: ${po.date}`, 150, 46)
    let y = 52
    Object.entries(groupByVendor(po.items)).forEach(([vendor, items]) => {
      doc.setFontSize(10); doc.setTextColor(31, 56, 100); doc.text(`Vendor: ${vendor || '-'}`, 14, y)
      autoTable(doc, {
        startY: y + 2,
        head: [['Sr', 'Tool', 'Dept', 'Qty', 'Best Price', 'Amount', 'Last']],
        body: items.map((it, i) => [String(i + 1), it.name, it.dept, String(it.qty), String(it.price), String(it.qty * it.price), it.lastPrice ? String(it.lastPrice) : '-']),
        styles: { fontSize: 8, cellPadding: 1.6 }, headStyles: { fillColor: [31, 56, 100] },
      })
      const sub = items.reduce((s, it) => s + it.qty * it.price, 0)
      y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY || y + 10
      doc.setFontSize(9); doc.setTextColor(90, 90, 90); doc.text(`Subtotal (${vendor}): Rs. ${sub.toLocaleString()}`, 14, y + 5)
      y += 12
      if (y > 255) { doc.addPage(); y = 20 }
    })
    let ly = y + 2
    doc.setFontSize(12); doc.setTextColor(0, 0, 0); doc.text(`Grand Total: Rs. ${po.total.toLocaleString()}`, 14, ly); ly += 8
    if (po.remarks) { doc.setFontSize(9); doc.setTextColor(90, 90, 90); doc.text(`Remarks: ${po.remarks}`, 14, ly); ly += 6 }
    doc.setFontSize(9); doc.setTextColor(90, 90, 90); doc.text(`Generated by: ${po.generatedBy || '-'}`, 14, ly)
    doc.save(`JP_${po.poNo}.pdf`)
    toast.success('PDF downloaded (grouped by vendor)')
  }

  const downloadExcel = (po: PurchaseOrder) => {
    const wb = XLSX.utils.book_new()
    Object.entries(groupByVendor(po.items)).forEach(([vendor, items]) => {
      const rows: Record<string, string | number>[] = items.map((it, i) => ({ Sr: i + 1, Tool: it.name, Department: it.dept, Qty: it.qty, Vendor: it.vendor, 'Best Price': it.price, Amount: it.qty * it.price, 'Last Price': it.lastPrice || '-' }))
      rows.push({ Sr: '', Tool: 'SUBTOTAL', Department: '', Qty: '', Vendor: vendor, 'Best Price': '', Amount: items.reduce((s, it) => s + it.qty * it.price, 0), 'Last Price': '' })
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), (vendor || 'Vendor').slice(0, 31))
    })
    const summary: Record<string, string | number>[] = [
      { Field: 'Company', Value: 'JP Gems & Jewels' },
      { Field: 'PO No', Value: po.poNo },
      { Field: 'Date', Value: po.date },
      { Field: 'Generated By', Value: po.generatedBy || '-' },
      { Field: 'Remarks', Value: po.remarks || '-' },
      { Field: 'Grand Total (Rs)', Value: po.total },
    ]
    Object.entries(groupByVendor(po.items)).forEach(([v, items]) =>
      summary.push({ Field: `Subtotal — ${v}`, Value: items.reduce((s, it) => s + it.qty * it.price, 0) }))
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summary), 'Summary')
    XLSX.writeFile(wb, `JP_${po.poNo}.xlsx`)
    toast.success('Excel downloaded (sheet per vendor)')
  }

  // Re-open a generated PO in the builder to fix a mistake / add more tools.
  const editOrder = (po: PurchaseOrder) => {
    const items: CartItem[] = po.items.map((it, i) => {
      const g = lookupGood(it.name)
      return { key: `${Date.now()}-${i}`, name: it.name, dept: it.dept, qty: it.qty, vendor: it.vendor, price: it.price, lastPrice: it.lastPrice, prices: g?.prices || { [it.vendor]: it.price } }
    })
    setCart(items); setRemarks(po.remarks || ''); setEditingPoNo(po.poNo); removeOrder(po.id)
    setTab(deptNames[0] || 'Setting')   // leave the Orders tab so the builder is visible
    toast.success(`Editing ${po.poNo} — change or add tools, then Update Purchase Order`)
  }

  const generatePO = () => {
    if (!cart.length) { toast.error('Add at least one tool to the purchase order'); return }
    const items: POItem[] = cart.map(c => ({ name: c.name, size: '', dept: c.dept, qty: c.qty, vendor: c.vendor, price: c.price, lastPrice: c.lastPrice }))
    const total = items.reduce((s, i) => s + i.qty * i.price, 0)
    const wasEditing = editingPoNo
    const poNo = wasEditing || `PO-${new Date().toISOString().slice(0, 7).replace('-', '')}-${String(orders.length + 1).padStart(3, '0')}`
    const po: PurchaseOrder = { id: `po-${Date.now()}`, poNo, date: new Date().toISOString().slice(0, 10), items, total, remarks: remarks.trim(), generatedBy: user?.name || 'Unknown' }
    addOrder(po); setCart([]); setRemarks(''); setEditingPoNo(null); setTab('__orders')
    toast.success(wasEditing ? `${poNo} updated` : `${poNo} generated — download Excel or PDF (grouped by vendor)`)
  }

  const exportPriceList = () => {
    const wb = XLSX.utils.book_new()
    const rows = goods.map(g => {
      const row: Record<string, string | number> = { Goods: g.name }
      allVendors.forEach(v => { row[v] = g.prices[v] ?? '-' })
      row['Best Vendor'] = g.bestVendor || '-'; row['Best Price'] = g.bestPrice || '-'
      return row
    })
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Price List')
    XLSX.writeFile(wb, `JP_Vendor_Price_List_${new Date().toISOString().slice(0, 10)}.xlsx`)
    toast.success('Price list exported')
  }

  const activeDept = depts.find(d => d.dept === activeTab)
  const deptItems = (activeDept?.items || []).filter(it => it.name.toLowerCase().includes(search.toLowerCase()))
  const priceItems = goods.filter(g => g.name.toLowerCase().includes(search.toLowerCase()))
  // Only show vendor columns that actually have a price among the visible goods.
  const visibleVendors = allVendors.filter(v => priceItems.some(g => g.prices[v] != null))

  return (
    <div className="space-y-5">
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2"><ShoppingBag size={22} className="text-primary-600" /> Purchase</h1>
          <p className="page-subtitle">JP Gems &amp; Jewels — department tool requirements, vendor price comparison &amp; purchase orders</p>
        </div>
        <button onClick={exportPriceList} className="btn-secondary text-sm flex items-center gap-2"><Download size={14} /> Export Price List</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card py-3 border-l-4 border-l-blue-500"><p className="text-xs text-steel-500">Departments</p><p className="text-2xl font-bold">{depts.length}</p></div>
        <div className="card py-3 border-l-4 border-l-green-500"><p className="text-xs text-steel-500">Goods in Price List</p><p className="text-2xl font-bold">{goods.length}</p></div>
        <div className="card py-3 border-l-4 border-l-primary-500"><p className="text-xs text-steel-500">Items in current PO</p><p className="text-2xl font-bold text-primary-600">{cart.length}</p></div>
        <div className="card py-3 border-l-4 border-l-purple-500"><p className="text-xs text-steel-500">Purchase Orders</p><p className="text-2xl font-bold">{orders.length}</p></div>
      </div>

      <div className="flex flex-wrap gap-1 bg-steel-100 rounded-xl p-1 w-fit">
        {deptNames.map(d => (
          <button key={d} onClick={() => { setTab(d); setSearch('') }}
            className={clsx('px-3 py-1.5 rounded-lg text-xs font-medium transition-colors', activeTab === d ? 'bg-white text-steel-800 shadow-sm' : 'text-steel-500 hover:text-steel-700')}>{d}</button>
        ))}
        <button onClick={() => { setTab('__price'); setSearch('') }} className={clsx('px-3 py-1.5 rounded-lg text-xs font-medium transition-colors', activeTab === '__price' ? 'bg-white text-steel-800 shadow-sm' : 'text-steel-500')}>Price List</button>
        <button onClick={() => { setTab('__orders'); setSearch('') }} className={clsx('px-3 py-1.5 rounded-lg text-xs font-medium transition-colors', activeTab === '__orders' ? 'bg-white text-steel-800 shadow-sm' : 'text-steel-500')}>Purchase Orders</button>
      </div>

      {activeTab !== '__orders' && (
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-white border border-steel-200 rounded-lg px-3 py-2 max-w-sm flex-1">
            <Search size={14} className="text-steel-400" />
            <input placeholder="Search tools..." value={search} onChange={e => setSearch(e.target.value)} className="bg-transparent text-sm outline-none flex-1" />
          </div>
          {activeTab !== '__price' && (
            <button onClick={openNewItem} className="btn-primary text-sm flex items-center gap-2 whitespace-nowrap"><Plus size={14} /> New Item</button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <div className={clsx(activeTab === '__orders' ? 'xl:col-span-3' : 'xl:col-span-2')}>
          {/* DEPARTMENT VIEW */}
          {activeTab !== '__price' && activeTab !== '__orders' && (
            <div className="card p-0 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="bg-steel-50 border-b border-steel-200">
                    <th className="table-header">#</th><th className="table-header">Tool</th>
                    <th className="table-header text-center">Monthly Qty</th>
                    <th className="table-header">Best Vendor</th><th className="table-header text-right">Best Price</th>
                    <th className="table-header text-right">Last Price</th><th className="table-header text-center">Qty</th>
                    <th className="table-header text-center">Actions</th>
                  </tr></thead>
                  <tbody>
                    {deptItems.length === 0 ? (
                      <tr><td colSpan={8} className="text-center text-steel-400 text-sm py-10">No tools yet. Click “New Item”.</td></tr>
                    ) : deptItems.map((it, i) => {
                      const g = lookupGood(it.name)
                      const key = rowKey(activeTab, it.name)
                      const vends = g ? Object.keys(g.prices) : []
                      const vendor = rowVendor[key] || g?.bestVendor || vends[0] || ''
                      const price = g ? (g.prices[vendor] ?? g.bestPrice) : 0
                      const last = lastPurchasePrice(it.name)
                      const qtyVal = rowQty[key] ?? (/^\d+$/.test(it.qty) ? it.qty : '1')
                      return (
                        <tr key={i} className="table-row-hover">
                          <td className="table-cell text-center text-xs text-steel-400">{i + 1}</td>
                          <td className="table-cell text-sm font-medium text-steel-800">{it.name}</td>
                          <td className="table-cell text-center text-sm text-steel-500">{it.qty || '—'}</td>
                          <td className="table-cell">
                            {vends.length > 0 ? (
                              <select value={vendor} onChange={e => setRowVendor({ ...rowVendor, [key]: e.target.value })} className="px-1 py-1 border border-steel-300 rounded text-xs bg-white" title="Compare &amp; choose vendor">
                                {vends.map(v => <option key={v} value={v}>{v} (₹{g!.prices[v]})</option>)}
                              </select>
                            ) : <span className="text-steel-300">—</span>}
                          </td>
                          <td className="table-cell text-right text-sm font-semibold text-green-700">{g && price ? `₹${price}` : '—'}</td>
                          <td className="table-cell text-right text-xs text-steel-500">{last != null ? `₹${last}` : '—'}</td>
                          <td className="table-cell text-center">
                            <input type="number" min={1} value={qtyVal} onChange={e => setRowQty({ ...rowQty, [key]: e.target.value })} className="w-14 px-1 py-0.5 border border-steel-300 rounded text-center text-xs" />
                          </td>
                          <td className="table-cell text-center">
                            <div className="flex items-center justify-center gap-1">
                              <button onClick={() => addToCart(it.name, activeTab, qtyVal, vendor, price, g?.prices || {}, last)} className="text-xs px-2 py-1 bg-primary-600 text-white rounded hover:bg-primary-700 flex items-center gap-1"><Plus size={11} /> PO</button>
                              <button onClick={() => openEditItem(it.name)} className="p-1.5 text-steel-500 hover:text-primary-600 hover:bg-primary-50 rounded" title="Edit item"><Edit3 size={13} /></button>
                              <button onClick={() => deleteItem(it.name)} className="p-1.5 text-steel-500 hover:text-red-600 hover:bg-red-50 rounded" title="Remove item"><Trash2 size={13} /></button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* PRICE LIST */}
          {activeTab === '__price' && (
            <div className="card p-0 overflow-hidden">
              <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
                <table className="w-full">
                  <thead className="sticky top-0"><tr className="bg-steel-50 border-b border-steel-200">
                    <th className="table-header">Goods</th>
                    {visibleVendors.map(v => <th key={v} className="table-header text-right">{v}</th>)}
                    <th className="table-header">Best</th><th className="table-header text-center">Action</th>
                  </tr></thead>
                  <tbody>
                    {priceItems.map(g => (
                      <tr key={g.id} className="table-row-hover">
                        <td className="table-cell text-sm font-medium text-steel-800">{g.name}</td>
                        {visibleVendors.map(v => (
                          <td key={v} className={clsx('table-cell text-right text-sm', g.bestVendor === v && g.prices[v] != null && 'font-bold text-green-700')}>
                            {g.prices[v] != null ? `₹${g.prices[v]}` : '—'}
                          </td>
                        ))}
                        <td className="table-cell text-xs">{g.bestVendor ? <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded">{g.bestVendor} ₹{g.bestPrice}</span> : '—'}</td>
                        <td className="table-cell text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button onClick={() => addToCart(g.name, 'Common', '1', g.bestVendor, g.bestPrice, g.prices, lastPurchasePrice(g.name))} className="text-xs px-2 py-1 bg-primary-600 text-white rounded hover:bg-primary-700">+ PO</button>
                            <button onClick={() => openEditItem(g.name)} className="p-1 text-steel-500 hover:text-primary-600 hover:bg-primary-50 rounded" title="Edit tool"><Edit3 size={13} /></button>
                            <button onClick={() => deleteGood(g.name)} className="p-1 text-steel-500 hover:text-red-600 hover:bg-red-50 rounded" title="Delete tool"><Trash2 size={13} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* PURCHASE ORDERS */}
          {activeTab === '__orders' && (
            <div className="card p-0 overflow-hidden">
              <table className="w-full">
                <thead><tr className="bg-steel-50 border-b border-steel-200">
                  <th className="table-header">PO No</th><th className="table-header">Date</th>
                  <th className="table-header">Generated By</th>
                  <th className="table-header text-center">Items</th><th className="table-header text-right">Total (₹)</th>
                  <th className="table-header text-center">Download (grouped by vendor)</th>
                </tr></thead>
                <tbody>
                  {orders.length === 0 ? (
                    <tr><td colSpan={6} className="text-center text-steel-400 text-sm py-10">No purchase orders yet.</td></tr>
                  ) : orders.map(o => (
                    <tr key={o.id} className="table-row-hover">
                      <td className="table-cell font-mono text-xs font-medium text-primary-700">{o.poNo}</td>
                      <td className="table-cell text-xs text-steel-500">{o.date}</td>
                      <td className="table-cell text-sm">{o.generatedBy || '—'}</td>
                      <td className="table-cell text-center text-sm">{o.items.length}</td>
                      <td className="table-cell text-right font-bold text-sm">₹{o.total.toLocaleString()}</td>
                      <td className="table-cell text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => downloadExcel(o)} className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 flex items-center gap-1"><FileSpreadsheet size={11} /> Excel</button>
                          <button onClick={() => downloadPdf(o)} className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 flex items-center gap-1"><FileText size={11} /> PDF</button>
                          <button onClick={() => editOrder(o)} className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded hover:bg-amber-200 flex items-center gap-1" title="Edit this PO — fix or add tools"><Edit3 size={11} /> Edit</button>
                          <button onClick={() => { if (window.confirm(`Delete ${o.poNo}?`)) { removeOrder(o.id); toast.success('Deleted') } }} className="p-1.5 text-steel-500 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* PO builder */}
        {activeTab !== '__orders' && (
          <div className="card h-fit sticky top-20">
            <h3 className="font-semibold text-steel-800 flex items-center gap-2 mb-3"><FileText size={16} /> {editingPoNo ? `Edit Purchase Order · ${editingPoNo}` : 'New Purchase Order'}</h3>
            {cart.length === 0 ? (
              <p className="text-sm text-steel-400 py-6 text-center">Add tools from the table (or “New Item”) to build a purchase order.</p>
            ) : (
              <div className="space-y-3">
                {cart.map(c => {
                  const vends = Object.keys(c.prices)
                  return (
                    <div key={c.key} className="border border-steel-200 rounded-lg p-2.5">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-xs font-medium text-steel-800 flex-1">{c.name}</p>
                        <button onClick={() => setCart(prev => prev.filter(x => x.key !== c.key))} className="text-steel-400 hover:text-red-600"><Trash2 size={13} /></button>
                      </div>
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        <div><label className="text-[10px] text-steel-500">Qty</label>
                          <input type="number" min={1} value={c.qty} onChange={e => setCartField(c.key, { qty: parseInt(e.target.value, 10) || 0 })} className="w-full px-1.5 py-1 border border-steel-300 rounded text-xs" /></div>
                        <div><label className="text-[10px] text-steel-500">Vendor</label>
                          <select value={c.vendor} onChange={e => chooseCartVendor(c.key, e.target.value)} className="w-full px-1 py-1 border border-steel-300 rounded text-xs bg-white">
                            {(vends.length ? vends : [c.vendor]).map(v => <option key={v} value={v}>{v}{c.prices[v] != null ? ` (₹${c.prices[v]})` : ''}</option>)}
                          </select></div>
                        <div><label className="text-[10px] text-steel-500">Price ₹</label>
                          <input type="number" value={c.price} onChange={e => setCartField(c.key, { price: parseFloat(e.target.value) || 0 })} className="w-full px-1.5 py-1 border border-steel-300 rounded text-xs" /></div>
                      </div>
                      <p className="text-[10px] text-steel-400 mt-1">Last: {c.lastPrice ? `₹${c.lastPrice}` : '—'} · Line: ₹{(c.qty * c.price).toLocaleString()}</p>
                    </div>
                  )
                })}
                <div>
                  <label className="text-xs font-medium text-steel-600">Remarks (added to the PO)</label>
                  <textarea value={remarks} onChange={e => setRemarks(e.target.value)} rows={2} placeholder="e.g. urgent, deliver by 10th…" className="w-full mt-1 px-2 py-1 border border-steel-300 rounded text-xs" />
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-steel-100">
                  <span className="text-sm font-semibold text-steel-700">Total</span>
                  <span className="text-lg font-bold text-primary-600">₹{cartTotal.toLocaleString()}</span>
                </div>
                <button onClick={generatePO} className="btn-primary w-full text-sm flex items-center justify-center gap-2"><Check size={15} /> {editingPoNo ? 'Update Purchase Order' : 'Generate Purchase Order'}</button>
                <button onClick={() => { setCart([]); setRemarks(''); setEditingPoNo(null) }} className="btn-secondary w-full text-xs">{editingPoNo ? 'Cancel Edit' : 'Clear'}</button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ===== ADD / EDIT ITEM MODAL ===== */}
      {showItemModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowItemModal(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-5 space-y-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-steel-800">{editingName ? 'Edit Item' : 'Add New Item'} — {activeTab}</h3>
              <button onClick={() => setShowItemModal(false)} className="text-steel-400 hover:text-steel-700 text-2xl leading-none">×</button>
            </div>

            <div>
              <label className="text-xs font-medium text-steel-600">New Item Name *</label>
              <input value={itemForm.name} onChange={e => setItemForm({ ...itemForm, name: e.target.value })} placeholder="e.g. GRAIN TOOL" className="form-input w-full mt-1" autoFocus />
            </div>

            {/* Vendors + prices (add any vendor for comparison) */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-steel-600">Vendors &amp; Prices (lowest is auto-picked)</label>
                <button onClick={addVendorRow} className="text-xs px-2 py-0.5 bg-primary-100 text-primary-700 rounded hover:bg-primary-200 flex items-center gap-1"><Plus size={11} /> Add Vendor</button>
              </div>
              <div className="space-y-2">
                {itemForm.vendors.map((v, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input value={v.vendor} onChange={e => setVendorRow(i, { vendor: e.target.value })} placeholder="Vendor name" className="form-input flex-1 text-sm" />
                    <input type="number" value={v.price} onChange={e => setVendorRow(i, { price: e.target.value })} placeholder="Price ₹" className="form-input w-28 text-sm" />
                    <button onClick={() => removeVendorRow(i)} className="text-steel-400 hover:text-red-600 p-1"><X size={14} /></button>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-steel-600">Qty</label>
                <input type="number" value={itemForm.qty} onChange={e => setItemForm({ ...itemForm, qty: e.target.value })} placeholder="1" className="form-input w-full mt-1" /></div>
              <div><label className="text-xs font-medium text-steel-600">Monthly Qty (optional)</label>
                <input type="number" value={itemForm.monthlyQty} onChange={e => setItemForm({ ...itemForm, monthlyQty: e.target.value })} className="form-input w-full mt-1" /></div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowItemModal(false)} className="btn-secondary text-sm">Cancel</button>
              <button onClick={saveItem} className="btn-primary text-sm">{editingName ? 'Save Changes' : 'Add to Table'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Purchase
